import React, { useState, useEffect, useMemo } from 'react';
import { purchasesService } from '../../services/purchasesService';
import { contactsService } from '../../services/contactsService';
import CreatePurchase from './CreatePurchase';
import PurchasesDashboard from './PurchasesDashboard';
import { Tab } from '@headlessui/react';
import {
  ShoppingBagIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import DataTable from '../common/DataTable';
import ConfirmationDialog from '../common/ConfirmationDialog';
import EditPurchaseDetailsModal from './EditPurchaseDetailsModal';
import PendingPayments from './PendingPayments';
import { ErrorBoundary } from 'react-error-boundary';
import PurchasePayment from './PurchasePayment';

export default function PurchaseManagement() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [supplierDetails, setSupplierDetails] = useState({});
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [purchaseToEditDetails, setPurchaseToEditDetails] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showCreatePurchase, setShowCreatePurchase] = useState(false);

  const fetchPurchases = async () => {
    try {
      const data = await purchasesService.getPurchases();
      console.log('Fetched purchases:', data);
      setPurchases(data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const suppliers = await contactsService.getContacts('supplier');
      const suppliersMap = {};
      suppliers.forEach(supplier => {
        suppliersMap[supplier.id] = supplier;
      });
      setSupplierDetails(suppliersMap);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handlePurchaseCreated = () => {
    fetchPurchases();
  };


  const handleDeletePurchase = (purchase) => {
    setPurchaseToDelete(purchase);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await purchasesService.deletePurchase(purchaseToDelete.id);
      setPurchases(prev => prev.filter(p => p.id !== purchaseToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting purchase:', error);
    }
  };

  const getPendingPurchases = () => {
    return purchases.filter(purchase => purchase.status === 'pending');
  };

  const getCompletedPurchases = () => {
    return purchases.filter(purchase => purchase.status === 'completed');
  };

  const getTotalPurchases = () => {
    return getCompletedPurchases().reduce((total, purchase) => total + purchase.totalAmount, 0);
  };

  const getAveragePurchaseValue = () => {
    const completed = getCompletedPurchases();
    return completed.length > 0 ? getTotalPurchases() / completed.length : 0;
  };

  const getPendingPayments = () => {
    return purchases.reduce((total, purchase) => {
      return total + (purchase.remainingBalance || 0);
    }, 0);
  };

  const handlePurchaseDetailsUpdated = (updatedPurchase) => {
    setPurchases(purchases.map(purchase =>
      purchase.id === updatedPurchase.id ? updatedPurchase : purchase
    ));
    setShowEditDetailsModal(false);
  };

  const tabs = [
    { name: 'Dashboard', icon: ChartBarIcon },
    { name: 'Pending Payments', icon: CurrencyDollarIcon },
  ];

  // Define columns configuration
  const columns = [
    {
      header: 'Supplier',
      accessorKey: 'supplierName',
    },
    {
      header: 'Items',
      accessorKey: 'items',
      cell: (info) => {
        const items = info.getValue();
        return (
          <div className="max-w-[200px] sm:max-w-none">
            {items.map(item =>
              `${item.vegetable} (${item.estimatedQuantity}kg)`
            ).join(', ')}
          </div>
        );
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => (
        <div className="flex flex-col sm:flex-row gap-2 min-w-[120px]">
          {info.row.original.status === 'pending' && (
            <>
              <button
                onClick={() => {
                  setSelectedPurchase(info.row.original);
                  setShowPaymentModal(true);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-primary-700 bg-primary-100 hover:bg-primary-200 
                  dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                Finalize
              </button>
              <button
                onClick={() => {
                  setPurchaseToEditDetails(info.row.original);
                  setShowEditDetailsModal(true);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-primary-700 bg-primary-100 hover:bg-primary-200 
                  dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDeletePurchase(info.row.original)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-red-700 bg-red-100 hover:bg-red-200 
                  dark:text-red-400 dark:bg-red-900/50 dark:hover:bg-red-900"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const pendingPurchases = useMemo(() => getPendingPurchases(), [purchases]);

  return (
    <div className="relative mb-24">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Purchase Management</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage your vegetable purchases from suppliers
          </p>
        </div>
      </div>

      <button
        onClick={() => setShowCreatePurchase(true)}
        className="fixed lg:bottom-6 bottom-20 right-6 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 z-40
          flex items-center justify-center group"
      >
        <ShoppingBagIcon className="h-6 w-6" />
        <span className="hidden md:block ml-2 mr-1 group-hover:mr-2 transition-all duration-200">
          Create Purchase
        </span>
      </button>

      <div className="mt-8">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) => `
                  ${selected
                    ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  } 
                  rounded-full px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-700 
                  transition-colors duration-150 ease-in-out flex items-center space-x-2
                `}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            <Tab.Panel>
              <PurchasesDashboard purchases={purchases} />
            </Tab.Panel>
            <Tab.Panel>
              <PendingPayments 
                purchases={purchases.filter(purchase => Number(purchase.remainingBalance) > 0)}
                supplierDetails={supplierDetails}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      <CreatePurchase
        isOpen={showCreatePurchase}
        onClose={() => setShowCreatePurchase(false)}
        onPurchaseCreated={handlePurchaseCreated}
      />

      <PurchasePayment
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        purchase={selectedPurchase}
        onPurchaseUpdated={handlePurchaseDetailsUpdated}
      />

      {showEditDetailsModal && (
        <EditPurchaseDetailsModal
          isOpen={showEditDetailsModal}
          onClose={() => setShowEditDetailsModal(false)}
          purchase={purchaseToEditDetails}
          onPurchaseUpdated={handlePurchaseDetailsUpdated}
        />
      )}

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Purchase"
        message={`Are you sure you want to delete the purchase from ${purchaseToDelete?.supplierName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}